<template>
  <v-dialog v-model="dialogVisible" max-width="700">
    <v-card class="edit-modal">
      <v-card-title class="edit-modal__header">
        <v-icon left>{{ isEditMode ? "mdi-pencil" : "mdi-plus" }}</v-icon>
        {{ isEditMode ? "Edit Stakeholder" : "Create New Stakeholder" }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="edit-modal__content">
        <v-form ref="form" v-model="formValid" lazy-validation>
          <!-- Name Field -->
          <v-text-field
            v-model="formData.name"
            label="Stakeholder Name"
            prepend-icon="mdi-account-multiple"
            :rules="nameRules"
            outlined
            required
          ></v-text-field>

          <!-- General Description Field -->
          <v-textarea
            v-model="formData.general_description"
            label="General Description"
            prepend-icon="mdi-text"
            outlined
            rows="3"
            auto-grow
            hint="Provide a brief description of this stakeholder group"
          ></v-textarea>

          <!-- Requirements Field -->
          <v-textarea
            v-model="formData.requirements"
            label="Requirements"
            prepend-icon="mdi-checkbox-multiple-marked-circle"
            outlined
            rows="3"
            auto-grow
            hint="List the key requirements for this stakeholder"
          ></v-textarea>

          <!-- Specificities Field -->
          <v-text-field
            v-model="formData.specificities"
            label="Specificities"
            prepend-icon="mdi-star"
            outlined
            hint="Special characteristics or notes"
            :disabled="isGPTGenerated"
          ></v-text-field>

          <!-- Related KPI Field -->
          <v-text-field
            v-model="formData.related_kpi"
            label="Related KPI"
            prepend-icon="mdi-target"
            outlined
            hint="Key Performance Indicator for this stakeholder"
          ></v-text-field>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="edit-modal__actions">
        <v-btn
          v-if="isEditMode"
          color="error"
          text
          @click="$emit('delete', stakeholder)"
        >
          <v-icon left>mdi-delete</v-icon>
          Delete
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="!formValid"
          :loading="saving"
          @click="save"
        >
          <v-icon left v-if="isEditMode">mdi-content-save</v-icon>
          <v-icon left v-else>mdi-plus</v-icon>
          {{ isEditMode ? "Save Changes" : "Create Stakeholder" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "StakeholderEditModal",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    stakeholder: {
      type: Object,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close", "save", "delete"],

  data() {
    return {
      formValid: true,
      formData: {
        name: "",
        general_description: "",
        requirements: "",
        specificities: "",
        related_kpi: "",
      },
      nameRules: [
        (v) => !!v || "Stakeholder name is required",
        (v) => (v && v.length >= 2) || "Name must be at least 2 characters",
      ],
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("close", val);
      },
    },

    isEditMode() {
      return this.stakeholder && this.stakeholder.id && !this.stakeholder.temp;
    },

    isGPTGenerated() {
      return (
        this.stakeholder?.specificities === "Stakeholder generated with GPT-4o"
      );
    },
  },

  methods: {
    close() {
      this.resetForm();
      this.dialogVisible = false;
    },

    resetForm() {
      this.formData = {
        name: "",
        general_description: "",
        requirements: "",
        specificities: "",
        related_kpi: "",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    populateForm() {
      if (this.stakeholder) {
        this.formData = {
          name: this.stakeholder.name || "",
          general_description: this.stakeholder.general_description || "",
          requirements: this.stakeholder.requirements || "",
          specificities: this.stakeholder.specificities || "",
          related_kpi: this.stakeholder.related_kpi || "",
        };
      } else {
        this.resetForm();
      }
    },

    async save() {
      if (this.$refs.form.validate()) {
        this.$emit("save", {
          ...this.stakeholder,
          ...this.formData,
        });
      }
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.populateForm();
      }
    },

    stakeholder: {
      handler() {
        if (this.value) {
          this.populateForm();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.edit-modal__header {
  background-color: var(--primaryColor);
  color: var(--backgroundColor) !important;
  align-items: center;
}

.edit-modal__content {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
